import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const STORAGE_KEY = "syntra-show-domain-tips";

type DomainGlossaryContextValue = {
  showDomainTips: boolean;
  setShowDomainTips: (value: boolean) => void;
  toggleDomainTips: () => void;
};

const DomainGlossaryContext = createContext<DomainGlossaryContextValue | null>(null);

function readStored(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === null) return true;
    return v === "true";
  } catch {
    return true;
  }
}

export function DomainGlossaryProvider({ children }: { children: ReactNode }) {
  const [showDomainTips, setShowDomainTipsState] = useState(readStored);

  const setShowDomainTips = useCallback((value: boolean) => {
    setShowDomainTipsState(value);
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      /* ignore */
    }
  }, []);

  const toggleDomainTips = useCallback(() => {
    setShowDomainTipsState((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value = useMemo<DomainGlossaryContextValue>(
    () => ({
      showDomainTips,
      setShowDomainTips,
      toggleDomainTips,
    }),
    [showDomainTips, setShowDomainTips, toggleDomainTips]
  );

  return <DomainGlossaryContext.Provider value={value}>{children}</DomainGlossaryContext.Provider>;
}

export function useDomainGlossary() {
  const ctx = useContext(DomainGlossaryContext);
  if (!ctx) {
    throw new Error("useDomainGlossary must be used within a DomainGlossaryProvider");
  }
  return ctx;
}

/** Use where provider is not guaranteed (e.g. Storybook) — tips hidden, setters no-op. */
export function useDomainGlossaryOptional(): DomainGlossaryContextValue {
  const ctx = useContext(DomainGlossaryContext);
  return (
    ctx ?? {
      showDomainTips: false,
      setShowDomainTips: () => {},
      toggleDomainTips: () => {},
    }
  );
}
